/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import RouterService from '@ember/routing/router-service';
import DS from 'ember-data';
import NewsletterModel from 'cio/models/newsletter';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import SegmentModel from 'cio/models/segment';

interface SendArgs {
  newsletter: NewsletterModel;
  allowSchedulingWithNewsletterConditionsNoRecipients: boolean;
}

export default class Send extends Component<SendArgs> {
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service router!: RouterService;
  @service store!: DS.Store;

  @tracked confirmSend = false;
  @tracked confirmCancel = false;
  @tracked isEditingSchedule = false;

  constructor(owner: unknown, args: SendArgs) {
    super(owner, args);
    this.loadSegmentsAndSchedule.perform();
  }

  get buildingSegments() {
    return (this.args.newsletter.segments.content as SegmentModel[])?.filterBy('isBuilding');
  }

  get segmentsReady() {
    return isEmpty(this.buildingSegments);
  }

  get canSend() {
    return this.args.newsletter.readyToSend && this.segmentsReady;
  }

  @computed(
    'canSend',
    'segmentsReady',
    'args.{newsletter.readyToSchedule,allowSchedulingWithNewsletterConditionsNoRecipients}',
  )
  get canSchedule() {
    return (
      this.canSend ||
      (this.segmentsReady &&
        this.args.newsletter.readyToSchedule &&
        this.args.allowSchedulingWithNewsletterConditionsNoRecipients)
    );
  }

  @computed('args.newsletter.rateLimitedSendDuration')
  get sendFinishedAt() {
    return moment().add(this.args.newsletter.rateLimitedSendDuration).format('MMM DD, YYYY [at] h:mm a (UTC Z)');
  }

  private loadSegmentsAndSchedule = dropTask(async () => {
    let { newsletter } = this.args;
    await newsletter.get('segments');
    await newsletter.get('schedule');
  });

  private sendNewsletter = dropTask(async () => {
    let { newsletter } = this.args;
    newsletter.set('updateType', 'send');

    try {
      await newsletter.save();

      // update setup guide "send campaign" step if not completed already
      // non-critical operation: launched in the background
      this.updateSetupGuideSendCampaignStep(newsletter);
      this.router.transitionTo('broadcasts.newsletter.overview.reports', newsletter);
    } catch (error) {
      this.apiErrors.report(error);
    }
  });

  private scheduleNewsletter = dropTask(async ({ timestamp, timezone, tzMatchEnabled }) => {
    let { newsletter } = this.args;

    newsletter.setProperties({
      updateType: 'schedule',
      scheduledAt: timestamp,
      tzMatchEnabled,
      timezone,
    });

    try {
      await newsletter.save();
      this.isEditingSchedule = false;
    } catch (error) {
      this.apiErrors.report(error);
    }
  });

  private removeSchedule = dropTask(async () => {
    let { newsletter } = this.args;

    newsletter.setProperties({
      updateType: 'schedule',
      scheduledAt: 0,
      tzMatchEnabled: false,
    });

    let schedule = await newsletter.get('schedule');

    if (schedule != null && !schedule.get('isEmpty')) {
      schedule.setProperties({
        scheduledAt: 0,
        originalScheduledAt: 0,
      });
    }

    try {
      await newsletter.save();
      this.isEditingSchedule = false;
      this.confirmCancel = false;
    } catch (error) {
      this.apiErrors.report(error);
    }
  });

  private async updateSetupGuideSendCampaignStep(newsletter: NewsletterModel) {
    let setupGuide = await this.store.findRecord('setup-guide', 'current');

    if (setupGuide.completedSteps['send-campaign']) {
      return;
    }

    await setupGuide.assignFirstMessage(newsletter);
  }

  @action
  confirmSendAction() {
    this.confirmSend = true;
    this.analytics.track('broadcast', 'send_started', {
      model: this.args.newsletter,
    });
  }

  @action
  sendAction(event: Event) {
    event?.preventDefault();

    this.sendNewsletter.perform();
  }

  @action
  schedule(options: { timezone: string; timestamp: number; tzMatchEnabled: boolean }) {
    this.scheduleNewsletter.perform(options);
  }

  @action
  removeScheduleAction(event: Event) {
    event?.preventDefault();

    this.removeSchedule.perform();
  }

  @action
  edit() {
    this.isEditingSchedule = true;
    this.analytics.track('broadcast', 'schedule_started', {
      model: this.args.newsletter,
    });
  }

  @action
  editSchedule() {
    this.isEditingSchedule = true;
    this.analytics.track('broadcast', 'scheduling_edit_started', {
      model: this.args.newsletter,
    });
  }

  @action
  cancel() {
    this.isEditingSchedule = false;
  }
}
